import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  ArrowBack,
  AttachFile,
  AlternateEmailOutlined,
  Download,
  LocalShipping,
  OpenInNew,
  SearchRounded,
  VisibilityOutlined,
} from '@mui/icons-material';
import Layout from './Layout';
import { getOrderTrackerDispatchedOrders, previewApcClaim, previewApcClaimEmail, searchOrderTracker, sendApcClaim } from '../services/api';
import { useSnackbar } from '../contexts/SnackbarContext';

const CLAIM_RULES = [
  'Loss claims: notify GS Couriers within 20 business days of the original delivery date.',
  'Damage or part-loss claims: notify within 3 business days of the original delivery date.',
  'Evidence: cost/manufacturing invoice or sales invoice; damage claims also need item and packaging photos.',
  'Caps: boxed freight liability up to GBP 100; Mail Pack, Courier Pack, and Lightweight up to GBP 50 unless increased liability applies.',
  'Excluded goods and prohibited goods should be checked before submission.',
];

const EMPTY_OBJECT = {};
const EMPTY_ARRAY = [];

const toDateInputValue = (date) => {
  const nextDate = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(nextDate.getTime())) return '';
  const year = nextDate.getFullYear();
  const month = String(nextDate.getMonth() + 1).padStart(2, '0');
  const day = String(nextDate.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getQueueDateRangePreset = (rangePreset = 'today') => {
  const end = new Date();
  const start = new Date();

  if (rangePreset === 'yesterday') {
    start.setDate(start.getDate() - 1);
    end.setDate(end.getDate() - 1);
  } else if (rangePreset === '3days') {
    start.setDate(start.getDate() - 2);
  } else if (rangePreset === '7days') {
    start.setDate(start.getDate() - 6);
  }

  return {
    startDate: toDateInputValue(start),
    endDate: toDateInputValue(end),
  };
};

const getDefaultQueueFilters = () => {
  return {
    ...getQueueDateRangePreset('today'),
    clientName: '',
    rangePreset: 'today',
  };
};

const formatDate = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('en-GB');
};

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const getClaimFilename = (claim) => {
  const reference = String(claim.orderNumber || claim.consignmentNumber || 'claim')
    .replace(/[^a-zA-Z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'claim';
  return `apc-${claim.claimType}-claim-${reference}.docx`;
};

const GBP_SYMBOL = '\u00a3';
const DEFAULT_GBP_CLAIM_VALUE = `${GBP_SYMBOL}0.00`;

const normalizeGbpClaimValue = (value) => {
  const cleaned = String(value || '').trim();
  if (!cleaned) return DEFAULT_GBP_CLAIM_VALUE;
  return cleaned.startsWith(GBP_SYMBOL) ? cleaned : `${GBP_SYMBOL}${cleaned}`;
};

const getClaimValueInput = (value) => {
  const text = String(value || DEFAULT_GBP_CLAIM_VALUE);
  return text.startsWith(GBP_SYMBOL) ? text.slice(GBP_SYMBOL.length).trimStart() : text;
};

const getItemKey = (item, index) => String(item?.id || item?.sku || item?.name || index);

const formatMoney = (value) => {
  if (value === null || value === undefined || value === '') return 'N/A';
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return String(value);
  return numeric.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' });
};

const formatOrderItemDescription = (item) => {
  const name = item?.name || item?.description || 'Item';
  const description = item?.description && item.description !== name ? item.description : '';
  const quantity = item?.quantity ? `x${item.quantity}` : '';
  return [quantity, name, description].filter(Boolean).join(' - ');
};

const buildGoodsDescription = (items, selectedKeys) => (
  items
    .filter((item, index) => selectedKeys.includes(getItemKey(item, index)))
    .map(formatOrderItemDescription)
    .join('\n')
);

const parseCourierEventTime = (event) => {
  const value = String(event?.dateTime || '').trim();
  const slashDate = value.match(/^(\d{2})\/(\d{2})\/(\d{4})\s+(\d{2}):(\d{2})(?::(\d{2}))?/);
  if (slashDate) {
    const [, day, month, year, hour, minute, second = '00'] = slashDate;
    const timestamp = Date.parse(`${year}-${month}-${day}T${hour}:${minute}:${second}`);
    if (Number.isFinite(timestamp)) return timestamp;
  }

  const dashDate = value.match(/^(\d{2})-(\d{2})-(\d{4})T(\d{2}):(\d{2})(?::(\d{2}))?/);
  if (dashDate) {
    const [, day, month, year, hour, minute, second = '00'] = dashDate;
    const timestamp = Date.parse(`${year}-${month}-${day}T${hour}:${minute}:${second}`);
    if (Number.isFinite(timestamp)) return timestamp;
  }

  if (event?.dateTimeIso) {
    const timestamp = Date.parse(event.dateTimeIso);
    if (Number.isFinite(timestamp)) return timestamp;
  }

  const fallback = Date.parse(value);
  return Number.isFinite(fallback) ? fallback : 0;
};

const sortCourierEvents = (events = []) => [...events].sort((a, b) => parseCourierEventTime(b) - parseCourierEventTime(a));

const getSortedTrackingEvents = (tracking) => sortCourierEvents(tracking?.events || []);

const getCurrentTrackingEvent = (tracking) => getSortedTrackingEvents(tracking)[0] || tracking?.latestEvent || null;

const getCurrentParcelStatus = (tracking, latestEvent) => (
  tracking?.currentStatus
  || tracking?.statusCategory
  || tracking?.summary?.statusCategory
  || latestEvent?.statusDescription
  || tracking?.sourceStatus
  || 'No courier status'
);

const formatFileSize = (size) => {
  if (!Number.isFinite(size)) return '';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const DetailRow = ({ label, value }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '138px 1fr' }, gap: 0.75, py: 0.55 }}>
    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.35 }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ overflowWrap: 'anywhere', color: '#1f2937', fontWeight: 600 }}>
      {value || 'N/A'}
    </Typography>
  </Box>
);

const DetailNote = ({ label, value }) => (
  <Box sx={{ py: 0.75 }}>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.35, mb: 0.65 }}>
      {label}
    </Typography>
    <Box
      sx={{
        p: 1.25,
        border: '1px solid #d7e0ea',
        borderRadius: 1,
        bgcolor: value ? '#f8fafc' : '#fff',
        color: '#1f2937',
        fontSize: 14,
        fontWeight: 650,
        lineHeight: 1.55,
        minHeight: 44,
        overflowWrap: 'anywhere',
        whiteSpace: 'pre-line',
      }}
    >
      {value || 'N/A'}
    </Box>
  </Box>
);

const SystemPanel = ({ title, status, children }) => {
  const color = status === 'ok' ? 'success' : status === 'error' ? 'error' : status === 'not_configured' ? 'default' : 'warning';
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        borderRadius: 1,
        height: '100%',
        bgcolor: '#fff',
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.035)',
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
          {title}
        </Typography>
        <Chip size="small" color={color} label={status || 'unknown'} />
      </Stack>
      {children}
    </Paper>
  );
};

const getItemImageUrl = (item) => item?.personalisedImageUrl || item?.productImageUrl || item?.imageUrl || '';

const OrderItemCards = ({ items = [], selectedKeys = [], onSelectionChange, selectable = false, source = 'mintsoft' }) => {
  if (!items.length) {
    return (
      <Alert severity="info" sx={{ borderRadius: 1 }}>
        No order items were returned for this order.
      </Alert>
    );
  }

  const toggleItem = (item, index) => {
    if (!onSelectionChange) return;
    const key = getItemKey(item, index);
    onSelectionChange(
      selectedKeys.includes(key)
        ? selectedKeys.filter((selectedKey) => selectedKey !== key)
        : [...selectedKeys, key]
    );
  };

  const allSelected = selectable && items.length > 0 && selectedKeys.length === items.length;

  return (
    <Stack spacing={1}>
      {selectable && (
        <Button
          variant="outlined"
          size="small"
          onClick={() => onSelectionChange(allSelected ? [] : items.map(getItemKey))}
          sx={{ alignSelf: 'flex-start', borderRadius: 1, fontWeight: 800 }}
        >
          {allSelected ? 'Clear all items' : 'Select all items'}
        </Button>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))',
          gap: 1.25,
        }}
      >
        {items.map((item, index) => {
          const key = getItemKey(item, index);
          const selected = selectedKeys.includes(key);
          const imageUrl = getItemImageUrl(item);
          const name = item.name || item.description || 'N/A';
          const description = item.description && item.description !== name ? item.description : '';

          return (
            <Paper
              key={key}
              variant="outlined"
              onClick={selectable ? () => toggleItem(item, index) : undefined}
              sx={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: 1,
                borderColor: selected ? 'success.light' : '#d7e0ea',
                bgcolor: selected ? '#f0fdf4' : '#fff',
                cursor: selectable ? 'pointer' : 'default',
                boxShadow: selected ? '0 8px 20px rgba(46, 125, 50, 0.12)' : '0 1px 2px rgba(15, 23, 42, 0.04)',
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  aspectRatio: '4 / 3',
                  bgcolor: '#f1f5f9',
                  borderBottom: '1px solid #e2e8f0',
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                {selectable && (
                  <Checkbox
                    checked={selected}
                    onChange={() => toggleItem(item, index)}
                    onClick={(event) => event.stopPropagation()}
                    inputProps={{ 'aria-label': `Select ${name}` }}
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      zIndex: 2,
                      bgcolor: 'rgba(255,255,255,0.9)',
                      borderRadius: '50%',
                    }}
                  />
                )}

                {imageUrl ? (
                  <Box
                    component="img"
                    src={imageUrl}
                    alt={name}
                    sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                ) : (
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                    No Image
                  </Typography>
                )}

                <Chip
                  label={`Qty ${item.quantity || 'N/A'}`}
                  size="small"
                  color="primary"
                  sx={{ position: 'absolute', left: 8, top: 8, fontWeight: 900 }}
                />
                {item.personalisedImageUrl && (
                  <Chip
                    label="Personalised"
                    size="small"
                    color="secondary"
                    sx={{ position: 'absolute', left: 8, bottom: 8, fontWeight: 800, bgcolor: '#e72d88' }}
                  />
                )}
              </Box>

              <Box sx={{ p: 1.25 }}>
                <Typography variant="caption" sx={{ display: 'block', color: '#475569', fontWeight: 900, textTransform: 'uppercase' }}>
                  {source} · {item.sku || 'No SKU'}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5, color: '#172033', fontWeight: 900, lineHeight: 1.35 }}>
                  {name}
                </Typography>
                {description && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, lineHeight: 1.35 }}>
                    {description}
                  </Typography>
                )}
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                  {item.unitPrice !== null && item.unitPrice !== undefined && <Chip label={formatMoney(item.unitPrice)} size="small" variant="outlined" />}
                  {item.artworkBarcode && <Chip label={`Artwork ${item.artworkBarcode}`} size="small" variant="outlined" />}
                </Stack>
              </Box>
            </Paper>
          );
        })}
      </Box>
    </Stack>
  );
};

const FactCell = ({ label, value, strong = false }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 800, textTransform: 'uppercase' }}>
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        mt: 0.25,
        color: '#0f172a',
        fontWeight: strong ? 900 : 700,
        overflowWrap: 'anywhere',
        lineHeight: 1.35,
      }}
    >
      {value || 'N/A'}
    </Typography>
  </Box>
);

const MintsoftOrderSummary = ({ order, sourceStatus, message }) => {
  const shippingAddress = order?.shippingAddress || EMPTY_OBJECT;
  const hasAddress = Boolean(shippingAddress.formatted);
  const statusLabel = order?.status || message || 'No Mintsoft status';
  const orderId = order?.id || 'N/A';

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 1.5,
        overflow: 'hidden',
        borderColor: '#d7e0ea',
        bgcolor: '#fff',
        boxShadow: '0 10px 24px rgba(15, 23, 42, 0.045)',
      }}
    >
      <Box
        sx={{
          px: 1.75,
          py: 1.5,
          borderBottom: '1px solid #e2e8f0',
          background: 'linear-gradient(135deg, #f8fbff 0%, #eef6ff 100%)',
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="subtitle1" sx={{ fontWeight: 950, color: '#172033', lineHeight: 1.2 }}>
                Mintsoft Order
              </Typography>
              <Chip
                size="small"
                color={sourceStatus === 'ok' ? 'success' : sourceStatus === 'error' ? 'error' : 'default'}
                label={sourceStatus || 'unknown'}
                sx={{ fontWeight: 900 }}
              />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
              Order ID {orderId} · {statusLabel}
            </Typography>
          </Box>
          {order?.orderUrl && (
            <Button
              component="a"
              href={order.orderUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="outlined"
              size="small"
              startIcon={<OpenInNew />}
              sx={{
                minHeight: 36,
                borderRadius: 1,
                fontWeight: 900,
                whiteSpace: 'nowrap',
                bgcolor: '#fff',
              }}
            >
              Open in Mintsoft
            </Button>
          )}
        </Stack>
      </Box>

      <Grid container spacing={0}>
        <Grid item xs={12} lg={7}>
          <Box sx={{ p: 1.5 }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                gap: 1.25,
              }}
            >
              <FactCell label="Mintsoft Order ID" value={order?.id} strong />
              <FactCell label="Order Number" value={order?.orderNumber} strong />
              <FactCell label="Status" value={statusLabel} />
              <FactCell label="Items" value={order?.itemCount} />
              <FactCell label="Courier Service" value={order?.courierServiceName} />
              <FactCell label="Mintsoft Tracking" value={order?.trackingNumber} strong />
              <FactCell label="Despatch Date" value={formatDate(order?.despatchDate)} />
              <FactCell label="External Ref" value={order?.externalOrderReference} />
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} lg={5}>
          <Box
            sx={{
              height: '100%',
              p: 1.5,
              borderTop: { xs: '1px solid #e2e8f0', lg: 'none' },
              borderLeft: { xs: 'none', lg: '1px solid #e2e8f0' },
              bgcolor: '#f8fafc',
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 900, textTransform: 'uppercase', letterSpacing: 0.35 }}>
              Shipping Address
            </Typography>
            {hasAddress ? (
              <Typography
                variant="body2"
                sx={{
                  mt: 0.75,
                  color: '#0f172a',
                  fontWeight: 750,
                  lineHeight: 1.55,
                  whiteSpace: 'pre-line',
                  overflowWrap: 'anywhere',
                }}
              >
                {shippingAddress.formatted}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, fontWeight: 650 }}>
                No shipping address was returned by Mintsoft for this order.
              </Typography>
            )}
          </Box>
        </Grid>
      </Grid>

      {order?.giftMessages && (
        <Box sx={{ px: 1.5, pb: 1.5 }}>
          <DetailNote label="Gift Message" value={order.giftMessages} />
        </Box>
      )}
    </Paper>
  );
};

const cleanCourierText = (value) => String(value || '')
  .replace(/\u0099/g, '™')
  .replace(/Â™/g, '™')
  .replace(/\u00a0/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const getEventDetails = (event = {}) => {
  if (Array.isArray(event.details)) return event.details;
  return event.details ? [event.details] : [];
};

const isRoyalMailEvent = (event = {}) => {
  const text = [
    event.courier,
    event.carrier,
    event.source,
    event.statusCode,
    event.statusCategory,
    event.statusDescription,
    event.comments,
    ...getEventDetails(event),
  ].map(cleanCourierText).join(' ');

  return /royal mail|tracked 24|tracked 48|category:|product:|destination:|we(?:'|’)ve got it|we have your item|\bEV[A-Z0-9]+/i.test(text);
};

const pushEventDetailRow = (rows, label, value) => {
  const cleanValue = cleanCourierText(value).replace(/\s+-\s*$/, '');
  if (!cleanValue) return;
  const alreadyShown = rows.some((row) => row.value.toLowerCase() === cleanValue.toLowerCase());
  if (!alreadyShown) rows.push({ label, value: cleanValue });
};

const pushLabelledCourierDetail = (rows, label, value) => {
  const cleanValue = cleanCourierText(value);
  if (!cleanValue) return;

  if (label === 'Category') {
    const noteMatch = cleanValue.match(/^(.*?)\s+-\s+(If .+)$/i);
    if (noteMatch) {
      pushEventDetailRow(rows, 'Category', noteMatch[1]);
      pushEventDetailRow(rows, 'Note', noteMatch[2]);
      return;
    }
  }

  if (label === 'Product') {
    const serviceMatch = cleanValue.match(/^(.*?)\s+-\s+((?:Aims|Aim|Expected).+)$/i);
    if (serviceMatch) {
      pushEventDetailRow(rows, 'Product', serviceMatch[1]);
      pushEventDetailRow(rows, 'Service', serviceMatch[2]);
      return;
    }
  }

  pushEventDetailRow(rows, label, cleanValue);
};

const appendCompositeRoyalMailDetails = (rows, text) => {
  const cleanText = cleanCourierText(text);
  const matches = [...cleanText.matchAll(/(?:^|\s+-\s)(Category|Product|Destination):\s*/gi)];
  if (!matches.length) return false;

  const prefix = cleanText.slice(0, matches[0].index).replace(/\s+-\s*$/, '');
  const prefixParts = prefix.split(/\s+-\s+/).map(cleanCourierText).filter(Boolean);
  prefixParts.forEach((part, index) => {
    if (index === 0 && prefixParts.length > 1) pushEventDetailRow(rows, 'Location', part);
    else if (/^If /i.test(part)) pushEventDetailRow(rows, 'Note', part);
    else pushEventDetailRow(rows, 'Status', part);
  });

  matches.forEach((match, index) => {
    const nextMatch = matches[index + 1];
    const start = match.index + match[0].length;
    const end = nextMatch ? nextMatch.index : cleanText.length;
    const value = cleanText.slice(start, end).replace(/\s+-\s*$/, '');
    pushLabelledCourierDetail(rows, match[1], value);
  });

  return true;
};

const appendRoyalMailDetail = (rows, value, event) => {
  const cleanValue = cleanCourierText(value);
  if (!cleanValue) return;
  if (appendCompositeRoyalMailDetails(rows, cleanValue)) return;

  const labelledMatch = cleanValue.match(/^(Category|Product|Destination):\s*(.+)$/i);
  if (labelledMatch) {
    pushLabelledCourierDetail(rows, labelledMatch[1], labelledMatch[2]);
    return;
  }

  if (cleanValue === cleanCourierText(event.location) || cleanValue === cleanCourierText(event.comments)) return;
  if (/^If /i.test(cleanValue) || /timeframe|guarantee|changed/i.test(cleanValue)) {
    pushEventDetailRow(rows, 'Note', cleanValue);
  } else if (/aims? to deliver|online tracking/i.test(cleanValue)) {
    pushEventDetailRow(rows, 'Service', cleanValue);
  } else if (/we(?:'|’)ve got it|delivered|received|in transit|ready/i.test(cleanValue)) {
    pushEventDetailRow(rows, 'Status', cleanValue);
  } else {
    pushEventDetailRow(rows, 'Details', cleanValue);
  }
};

const getRoyalMailEventRows = (event = {}) => {
  const rows = [];
  pushEventDetailRow(rows, 'Location', event.location);
  pushEventDetailRow(rows, 'Signed by', event.signedBy);
  getEventDetails(event).forEach((detail) => appendRoyalMailDetail(rows, detail, event));
  return rows;
};

const EventDetail = ({ event = {} }) => {
  const details = [
    event.location,
    event.signedBy ? `Signed by ${event.signedBy}` : null,
    getEventDetails(event).length ? null : event.comments,
    ...getEventDetails(event),
  ].map(cleanCourierText).filter(Boolean);
  const uniqueDetails = [...new Set(details)];

  if (isRoyalMailEvent(event)) {
    const rows = getRoyalMailEventRows(event);
    if (!rows.length) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
          N/A
        </Typography>
      );
    }

    return (
      <Stack spacing={0.55} sx={{ maxWidth: '100%' }}>
        {rows.map((row) => (
          <Box
            key={`${row.label}-${row.value}`}
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '92px minmax(0, 1fr)' },
              gap: { xs: 0.1, sm: 1 },
              alignItems: 'start',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: '#64748b',
                fontWeight: 900,
                textTransform: 'uppercase',
                letterSpacing: 0.25,
                lineHeight: 1.35,
              }}
            >
              {row.label}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#172033',
                fontWeight: row.label === 'Message' ? 750 : 650,
                lineHeight: 1.45,
                overflowWrap: 'anywhere',
              }}
            >
              {row.value}
            </Typography>
          </Box>
        ))}
      </Stack>
    );
  }

  return (
    <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
      {uniqueDetails.join(' - ') || 'N/A'}
    </Typography>
  );
};

const EventTable = ({ events }) => {
  const sortedEvents = sortCourierEvents(events || []);

  if (!sortedEvents.length) {
    return (
      <Box
        sx={{
          height: '100%',
          minHeight: 320,
          py: 5,
          px: 2,
          bgcolor: '#f8fafc',
          textAlign: 'center',
          display: 'grid',
          placeItems: 'center',
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#334155' }}>
          No courier events returned
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Check the tracking number or try again after the courier has updated scans.
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer
      sx={{
        height: '100%',
        minHeight: 0,
        overflow: 'auto',
        bgcolor: '#fff',
      }}
    >
      <Table stickyHeader size="small" aria-label="Courier tracking events">
        <TableHead>
          <TableRow>
            {['Scan Time', 'Event', 'Details'].map((heading) => (
              <TableCell
                key={heading}
                sx={{
                  bgcolor: '#f8fafc',
                  color: '#475569',
                  fontSize: 11,
                  fontWeight: 900,
                  textTransform: 'uppercase',
                  letterSpacing: 0.35,
                  borderBottom: '1px solid #dbe5f1',
                  py: 1.15,
                }}
              >
                {heading}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedEvents.map((event, index) => (
            <TableRow
              key={`${event.dateTime || index}-${event.statusCode || ''}-${event.statusDescription || ''}-${event.itemNumber || ''}-${event.trackingNumber || ''}`}
              hover
              sx={{
                bgcolor: index === 0 ? '#f0fdf4' : '#fff',
                transition: 'background-color 140ms ease',
                '&:hover': { bgcolor: index === 0 ? '#ecfdf5' : '#f8fafc' },
                '&:last-child td': { borderBottom: 0 },
              }}
            >
              <TableCell sx={{ width: 190, verticalAlign: 'top', borderColor: '#edf2f7', py: 1.35 }}>
                <Typography variant="body2" sx={{ fontWeight: 800, color: '#0f172a', whiteSpace: 'nowrap' }}>
                  {event.dateTime || formatDate(event.dateTimeIso)}
                </Typography>
                {index === 0 && (
                  <Chip
                    label="Latest"
                    size="small"
                    color="success"
                    sx={{ mt: 0.75, height: 22, fontWeight: 800 }}
                  />
                )}
              </TableCell>
              <TableCell sx={{ width: 260, verticalAlign: 'top', borderColor: '#edf2f7', py: 1.35 }}>
                <Typography variant="body2" sx={{ fontWeight: 900, color: '#172033' }}>
                  {event.statusDescription || 'Status update'}
                </Typography>
                {event.statusCode && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                    Code {event.statusCode}
                  </Typography>
                )}
              </TableCell>
              <TableCell sx={{ verticalAlign: 'top', borderColor: '#edf2f7', py: 1.35 }}>
                <EventDetail event={event} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const ProofOfDeliveryPanel = ({ proof, trackingUrl = '' }) => {
  const deliveryImageUrl = proof?.imageUrl || '';
  const signatureImageUrl = proof?.signatureImageUrl || '';
  const proofError = proof?.signatureEndpointError || proof?.endpointError || '';
  const hasReferenceOnlyProof = Boolean(
    !deliveryImageUrl
    && !signatureImageUrl
    && (proof?.signatureImageId || proof?.signatureDateTime)
  );
  const [failedImages, setFailedImages] = useState({});

  React.useEffect(() => {
    setFailedImages({});
  }, [deliveryImageUrl, signatureImageUrl]);

  if (!proof?.available) {
    return (
      <Paper
        variant="outlined"
        sx={{
          height: '100%',
          minHeight: 360,
          p: 2,
          borderRadius: 1.5,
          borderColor: '#dbe5f1',
          bgcolor: '#f8fafc',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#334155' }}>
          Delivery Proof
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
          {proofError || 'The courier did not return proof image, signature, or useful GPS details for this lookup.'}
        </Typography>
      </Paper>
    );
  }

  const hasMap = Boolean(proof.mapUrl);
  const proofImages = [
    {
      key: 'delivery',
      label: 'Delivery Image',
      src: deliveryImageUrl,
      alt: 'Proof of delivery photo',
    },
    {
      key: 'signature',
      label: 'Signature',
      src: signatureImageUrl,
      alt: 'Signature proof',
    },
  ].filter((image) => image.src && !failedImages[image.key]);
  const markImageFailed = (key) => {
    setFailedImages((prev) => ({ ...prev, [key]: true }));
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        height: '100%',
        minHeight: 360,
        p: 0,
        borderRadius: 1.5,
        borderColor: '#dbe5f1',
        bgcolor: '#fff',
        boxShadow: '0 10px 24px rgba(15, 23, 42, 0.04)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        spacing={1}
        sx={{
          p: 1.75,
          borderBottom: '1px solid #e2e8f0',
          bgcolor: '#fbfdff',
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 950, color: '#172033', lineHeight: 1.2 }}>
            Delivery Proof
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
            {hasReferenceOnlyProof
              ? 'Royal Mail proof images require postcode verification on the Royal Mail tracking website.'
              : 'POD image, GPS and recipient details from the courier.'}
          </Typography>
        </Box>
        {trackingUrl && hasReferenceOnlyProof && (
          <Button
            component="a"
            href={trackingUrl}
            target="_blank"
            rel="noopener noreferrer"
            variant="contained"
            startIcon={<OpenInNew />}
            sx={{ borderRadius: 1, fontWeight: 800, alignSelf: { xs: 'flex-start', sm: 'center' }, flexShrink: 0 }}
          >
            Verify on Royal Mail
          </Button>
        )}
        {hasMap && !hasReferenceOnlyProof && (
          <Button
            component="a"
            href={proof.mapUrl}
            target="_blank"
            rel="noopener noreferrer"
            variant="outlined"
            startIcon={<OpenInNew />}
            sx={{ borderRadius: 1, fontWeight: 800, alignSelf: { xs: 'flex-start', sm: 'center' }, flexShrink: 0 }}
          >
            Open GPS
          </Button>
        )}
      </Stack>

      <Box
        sx={{
          p: 1.75,
          overflow: 'auto',
          minHeight: 0,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: proofImages.length > 1
              ? { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }
              : '1fr',
            gap: 1.25,
          }}
        >
          {proofImages.length > 0 ? (
            proofImages.map((image) => (
              <Box key={image.key} sx={{ minWidth: 0 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mb: 0.6, fontWeight: 900, textTransform: 'uppercase' }}
                >
                  {image.label}
                </Typography>
                <Box
                  sx={{
                    width: '100%',
                    aspectRatio: image.key === 'signature' ? '16 / 9' : '4 / 3',
                    maxHeight: image.key === 'signature' ? 240 : { xs: 420, md: 520, xl: 600 },
                    borderRadius: 1.25,
                    border: '1px solid #dbe5f1',
                    bgcolor: '#f8fafc',
                    overflow: 'hidden',
                    display: 'grid',
                    placeItems: 'center',
                  }}
                >
                  <Box
                    component="img"
                    src={image.src}
                    alt={image.alt}
                    onError={() => markImageFailed(image.key)}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      bgcolor: '#fff',
                      display: 'block',
                    }}
                  />
                </Box>
              </Box>
            ))
          ) : (
            <Box
            sx={{
              width: '100%',
              aspectRatio: '4 / 3',
              maxHeight: { xs: 420, md: 520, xl: 600 },
              borderRadius: 1.25,
              border: '1px solid #dbe5f1',
              bgcolor: '#f8fafc',
              overflow: 'hidden',
              display: 'grid',
              placeItems: 'center',
              }}
            >
              <Box sx={{ px: 2, textAlign: 'center' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#64748b' }}>
                  {hasReferenceOnlyProof ? 'Royal Mail Image Not Supplied' : 'Image Not Available'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {hasReferenceOnlyProof
                    ? 'Enter the delivery postcode on Royal Mail to view the delivery photo and signature.'
                    : 'Courier returned proof details without an image.'}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr',
              gap: 1.1,
              p: 1.25,
              borderRadius: 1.25,
              bgcolor: '#f8fafc',
              border: '1px solid #e2e8f0',
            }}
          >
            <FactCell label="Signed By" value={proof.signedBy} strong />
            <FactCell label="Captured At" value={formatDate(proof.signatureDateTime)} />
            <FactCell label="Image Reference" value={proof.signatureImageId} />
            <FactCell label="GPS Location" value={hasMap ? `${proof.latitude}, ${proof.longitude}` : 'N/A'} />
            <FactCell label="Company" value={proof.companyName} />
            <FactCell label="Contact" value={proof.contactName} />
            <FactCell label="Postcode" value={proof.postcode} />
            <FactCell label="Address" value={proof.addressLines?.join(', ')} />
          </Box>
      </Box>
    </Paper>
  );
};

const StatusOverview = ({ localOrder, tracking, latestEvent, result, canGenerateApcClaim, onClaimOpen }) => {
  const latestStatus = getCurrentParcelStatus(tracking, latestEvent);
  const isDelivered = Boolean(tracking?.isDelivered || String(latestStatus).toLowerCase().includes('delivered'));
  const mintsoftTracking = result.systems?.mintsoft?.order?.trackingNumber;
  const isRoyalMail = tracking?.courier === 'royal_mail';
  const royalMailClaimUrl = tracking?.claimUrl || result.actions?.royalMailClaimUrl;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 1,
        borderColor: isDelivered ? '#86efac' : '#d7e0ea',
        bgcolor: isDelivered ? '#f0fdf4' : '#fff',
        boxShadow: '0 8px 24px rgba(15, 23, 42, 0.06)',
      }}
    >
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} lg={4}>
          <Stack direction={{ xs: 'column', sm: 'row', lg: 'column' }} spacing={1.25} alignItems={{ xs: 'flex-start', sm: 'center', lg: 'flex-start' }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 900, textTransform: 'uppercase' }}>
                Current parcel status
              </Typography>
              <Typography variant="h4" sx={{ mt: 0.5, fontSize: { xs: 26, md: 32 }, fontWeight: 950, color: '#0f172a', lineHeight: 1.1 }}>
                {latestStatus}
              </Typography>
            </Box>
            <Stack
              direction="row"
              spacing={1}
              useFlexGap
              flexWrap="wrap"
              alignItems="flex-start"
              sx={{ '& .MuiButton-root': { width: 'auto', flex: '0 0 auto' } }}
            >
              {isRoyalMail && royalMailClaimUrl ? (
                <Button
                  component="a"
                  href={royalMailClaimUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="contained"
                  size="small"
                  startIcon={<LocalShipping />}
                  sx={{
                    borderRadius: 1,
                    fontWeight: 900,
                    minHeight: 34,
                    minWidth: 0,
                    px: 1.45,
                    whiteSpace: 'nowrap',
                    color: '#fff',
                    '&:hover, &:active, &:focus': {
                      color: '#fff',
                    },
                    '& .MuiButton-startIcon, & .MuiButton-startIcon svg': {
                      color: '#fff',
                    },
                  }}
                >
                  Make a Claim
                </Button>
              ) : (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<LocalShipping />}
                  onClick={onClaimOpen}
                  disabled={!canGenerateApcClaim}
                  sx={{
                    borderRadius: 1,
                    fontWeight: 900,
                    minHeight: 34,
                    minWidth: 0,
                    px: 1.45,
                    whiteSpace: 'nowrap',
                    color: '#fff',
                    '&:hover, &:active, &:focus': {
                      color: '#fff',
                    },
                    '& .MuiButton-startIcon, & .MuiButton-startIcon svg': {
                      color: '#fff',
                    },
                  }}
                >
                  Make a Claim
                </Button>
              )}
              {isRoyalMail && tracking?.trackingUrl && (
                <Button
                  component="a"
                  href={tracking.trackingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  size="small"
                  startIcon={<OpenInNew />}
                  sx={{
                    borderRadius: 1,
                    fontWeight: 900,
                    minHeight: 34,
                    minWidth: 0,
                    px: 1.45,
                    whiteSpace: 'nowrap',
                  }}
                >
                  Track Royal Mail
                </Button>
              )}
            </Stack>
          </Stack>
          <Box sx={{ mt: 0.75 }}>
            {latestEvent ? (
              <EventDetail event={latestEvent} />
            ) : (
              <Typography variant="body2" color="text.secondary">
                {cleanCourierText(tracking?.message) || 'Courier lookup has no latest scan yet.'}
              </Typography>
            )}
          </Box>
        </Grid>

        <Grid item xs={12} lg={8}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(3, minmax(0, 1fr))' },
              gap: 1.5,
              p: 1.5,
              borderRadius: 1,
              bgcolor: '#fff',
              border: '1px solid #dbe5f1',
            }}
          >
            <FactCell label="Order" value={localOrder?.orderNumber} strong />
            <FactCell label="Client" value={localOrder?.clientName} />
            <FactCell label="Courier" value={localOrder?.shippingCarrier || tracking?.courier} />
            <FactCell label="Dispatch Date" value={formatDate(localOrder?.dispatchedDate)} />
            <FactCell label="Linx Tracking" value={tracking?.trackingNumber || localOrder?.tracking} strong />
            <FactCell label="Mintsoft Tracking" value={mintsoftTracking} />
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

const CourierTrackingPanel = ({ tracking, localOrder }) => {
  const isRoyalMail = tracking?.courier === 'royal_mail';

  return (
    <Box sx={{ width: '100%' }}>
      {isRoyalMail && tracking?.message && (
        <Alert
          severity={tracking?.sourceStatus === 'error' ? 'warning' : 'info'}
          action={tracking?.trackingUrl ? (
            <Button
              component="a"
              href={tracking.trackingUrl}
              target="_blank"
              rel="noopener noreferrer"
              color="inherit"
              size="small"
              startIcon={<OpenInNew />}
              sx={{ fontWeight: 900 }}
            >
              Open Royal Mail
            </Button>
          ) : null}
          sx={{ borderRadius: 1, mb: 1.5 }}
        >
          {cleanCourierText(tracking.message)}
        </Alert>
      )}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'minmax(0, 1.45fr) minmax(300px, 0.85fr)',
            xl: 'minmax(0, 1.6fr) minmax(360px, 0.8fr)',
          },
          gap: 1.5,
          alignItems: 'stretch',
        }}
      >
        <Paper
          variant="outlined"
          sx={{
            minWidth: 0,
            minHeight: 420,
            borderRadius: 1.5,
            borderColor: '#dbe5f1',
            bgcolor: '#fff',
            boxShadow: '0 10px 24px rgba(15, 23, 42, 0.04)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            spacing={1}
            sx={{
              p: 1.75,
              borderBottom: '1px solid #e2e8f0',
              bgcolor: '#fbfdff',
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 950, color: '#172033', lineHeight: 1.2 }}>
                Courier Events
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                {tracking?.events?.length || 0} scans for {tracking?.trackingNumber || localOrder?.tracking || 'N/A'}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ alignSelf: { xs: 'flex-start', sm: 'center' }, flexShrink: 0 }}>
              {isRoyalMail && tracking?.trackingUrl && (
                <Button
                  component="a"
                  href={tracking.trackingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  size="small"
                  startIcon={<OpenInNew />}
                  sx={{ borderRadius: 1, fontWeight: 900 }}
                >
                  Open Royal Mail
                </Button>
              )}
              <Chip
                label={tracking?.sourceStatus || 'unknown'}
                color={tracking?.sourceStatus === 'ok' ? 'success' : tracking?.sourceStatus === 'error' ? 'error' : 'default'}
                sx={{ fontWeight: 800 }}
              />
            </Stack>
          </Stack>
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <EventTable events={getSortedTrackingEvents(tracking)} />
          </Box>
        </Paper>
        <Box sx={{ minWidth: 0, minHeight: 0 }}>
          <ProofOfDeliveryPanel proof={tracking?.proofOfDelivery} trackingUrl={tracking?.trackingUrl} />
        </Box>
      </Box>
    </Box>
  );
};

const OrderDetailsTabs = ({
  activeTab,
  onTabChange,
  localOrder,
  result,
  tracking,
}) => {
  const mintsoftOrder = result.systems?.mintsoft?.order;
  const mintsoftItems = mintsoftOrder?.orderItems || EMPTY_ARRAY;
  const kornitxItems = result.systems?.kornitx?.order?.items || EMPTY_ARRAY;

  return (
    <Box sx={{ border: '1px solid #d7e0ea', borderRadius: 1, bgcolor: '#fff', overflow: 'hidden' }}>
      <Tabs
        value={activeTab}
        onChange={(_, value) => onTabChange(value)}
        variant="scrollable"
        scrollButtons="auto"
        aria-label="Order tracker detail tabs"
        sx={{
          px: 1,
          minHeight: 48,
          bgcolor: '#f8fafc',
          borderBottom: '1px solid #d7e0ea',
          '& .MuiTab-root': {
            minHeight: 48,
            fontWeight: 900,
            textTransform: 'none',
          },
        }}
      >
        <Tab value="courier" label="Courier Events" />
        <Tab value="mintsoft" label="Mintsoft" />
        <Tab value="linx" label="Linx" />
        <Tab value="kornitx" label="KornitX" />
      </Tabs>

      <Box sx={{ p: 1.5 }}>
        {activeTab === 'courier' && (
          <CourierTrackingPanel tracking={tracking} localOrder={localOrder} />
        )}

        {activeTab === 'mintsoft' && (
          <Stack spacing={1.5}>
            <MintsoftOrderSummary
              order={mintsoftOrder}
              sourceStatus={result.systems?.mintsoft?.sourceStatus}
              message={result.systems?.mintsoft?.message}
            />

            <Box>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.75} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 950, color: '#172033' }}>
                  Mintsoft Order Items
                </Typography>
                <Chip
                  size="small"
                  variant="outlined"
                  label={`${mintsoftItems.length} item${mintsoftItems.length === 1 ? '' : 's'}`}
                  sx={{ fontWeight: 800, bgcolor: '#fff' }}
                />
              </Stack>
              <OrderItemCards items={mintsoftItems} source="Mintsoft" />
            </Box>
          </Stack>
        )}

        {activeTab === 'linx' && (
          <SystemPanel title="Linx" status={result.systems?.linx?.sourceStatus}>
            <Grid container spacing={1.25}>
              <Grid item xs={12} sm={6}>
                <DetailRow label="Order Number" value={localOrder?.orderNumber} />
                <DetailRow label="Client" value={localOrder?.clientName} />
                <DetailRow label="Category" value={localOrder?.category} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailRow label="Status" value={localOrder?.orderStatus} />
                <DetailRow label="Dispatch Date" value={formatDate(localOrder?.dispatchedDate)} />
                <DetailRow label="Tracking" value={localOrder?.tracking} />
                <DetailRow label="Issue" value={localOrder?.issueStatus} />
              </Grid>
            </Grid>
          </SystemPanel>
        )}

        {activeTab === 'kornitx' && (
          <Stack spacing={1.5}>
            <SystemPanel title="KornitX" status={result.systems?.kornitx?.sourceStatus}>
              <Grid container spacing={1.25}>
                <Grid item xs={12} sm={6}>
                  <DetailRow label="External Ref" value={result.systems?.kornitx?.order?.externalRef} />
                  <DetailRow label="Status" value={result.systems?.kornitx?.order?.status || result.systems?.kornitx?.message} />
                  <DetailRow label="Created" value={formatDate(result.systems?.kornitx?.order?.createdAt)} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DetailRow label="Items" value={result.systems?.kornitx?.order?.itemCount} />
                  <DetailRow label="Carrier" value={result.systems?.kornitx?.order?.shippingCarrier} />
                  <DetailRow label="Method" value={result.systems?.kornitx?.order?.shippingMethod} />
                </Grid>
              </Grid>
            </SystemPanel>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 950, color: '#172033', mb: 1 }}>
                KornitX Order Items
              </Typography>
              <OrderItemCards items={kornitxItems} source="KornitX" />
            </Box>
          </Stack>
        )}
      </Box>
    </Box>
  );
};

const DispatchedOrdersQueue = ({
  filters,
  onFilterChange,
  onRangePresetChange,
  onApplyFilters,
  loading,
  orders,
  clientOptions,
  onTrack,
}) => (
  <Paper
    variant="outlined"
    sx={{
      borderRadius: 1,
      bgcolor: '#fff',
      borderColor: '#d7e0ea',
      overflow: 'hidden',
      boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
    }}
  >
    <Box sx={{ p: 2, borderBottom: '1px solid #d7e0ea', bgcolor: '#fbfdff' }}>
      <Stack direction={{ xs: 'column', xl: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xl: 'center' }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 950, color: '#172033' }}>
            Dispatched Orders
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Daily CS queue for recently dispatched parcels. Filter, then open tracking details in one click.
          </Typography>
        </Box>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          component="form"
          onSubmit={(event) => {
            event.preventDefault();
            onApplyFilters();
          }}
          sx={{ minWidth: { xl: 720 } }}
        >
          <ToggleButtonGroup
            value={filters.rangePreset || 'custom'}
            exclusive
            onChange={(_, value) => value && onRangePresetChange(value)}
            size="small"
            aria-label="Dispatched date range"
            sx={{
              '& .MuiToggleButton-root': {
                px: 1.2,
                fontWeight: 900,
                borderRadius: '4px !important',
                whiteSpace: 'nowrap',
              },
            }}
          >
            <ToggleButton value="today">Today</ToggleButton>
            <ToggleButton value="yesterday">Yesterday</ToggleButton>
            <ToggleButton value="3days">3 Days</ToggleButton>
            <ToggleButton value="7days">7 Days</ToggleButton>
          </ToggleButtonGroup>
          <TextField
            label="From"
            type="date"
            value={filters.startDate}
            onChange={(event) => {
              onFilterChange('startDate', event.target.value);
              onFilterChange('rangePreset', 'custom');
            }}
            size="small"
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
          />
          <TextField
            label="To"
            type="date"
            value={filters.endDate}
            onChange={(event) => {
              onFilterChange('endDate', event.target.value);
              onFilterChange('rangePreset', 'custom');
            }}
            size="small"
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
          />
          <FormControl size="small" sx={{ minWidth: { sm: 240 }, flex: 1 }}>
            <InputLabel id="dispatched-client-filter-label" shrink>Client Name</InputLabel>
            <Select
              labelId="dispatched-client-filter-label"
              label="Client Name"
              value={filters.clientName}
              displayEmpty
              renderValue={(selected) => selected || 'All Clients'}
              onChange={(event) => onFilterChange('clientName', event.target.value)}
            >
              <MenuItem value="">All Clients</MenuItem>
              {filters.clientName && !clientOptions.includes(filters.clientName) && (
                <MenuItem value={filters.clientName}>{filters.clientName}</MenuItem>
              )}
              {clientOptions.map((clientName) => (
                <MenuItem key={clientName} value={clientName}>
                  {clientName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : <SearchRounded />}
            sx={{
              height: 40,
              minWidth: 112,
              px: 2.25,
              borderRadius: 1,
              fontWeight: 900,
              alignSelf: { xs: 'stretch', sm: 'center' },
              whiteSpace: 'nowrap',
            }}
          >
            Filter
          </Button>
        </Stack>
      </Stack>
    </Box>

    <Box sx={{ px: 2, py: 1.25, display: 'flex', alignItems: 'center', gap: 1, borderBottom: '1px solid #edf2f7' }}>
      <Chip label={`${orders.length} order${orders.length === 1 ? '' : 's'}`} color="primary" variant="outlined" sx={{ fontWeight: 900 }} />
      <Typography variant="body2" color="text.secondary">
        Showing latest dispatched orders in selected range.
      </Typography>
    </Box>

    <TableContainer sx={{ maxHeight: 'calc(100vh - 390px)', minHeight: 260 }}>
      <Table stickyHeader size="small" aria-label="Dispatched orders">
        <TableHead>
          <TableRow>
            {['Order', 'Client', 'Dispatched', 'Courier', 'Tracking', 'Action'].map((heading) => (
              <TableCell
                key={heading}
                sx={{
                  bgcolor: '#f8fafc',
                  color: '#475569',
                  fontSize: 12,
                  fontWeight: 900,
                  textTransform: 'uppercase',
                  borderBottom: '1px solid #dbe5f1',
                }}
              >
                {heading}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading && (
            <TableRow>
              <TableCell colSpan={6}>
                <Box sx={{ py: 5, textAlign: 'center' }}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Loading dispatched orders...
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
          )}
          {!loading && orders.map((order) => (
            <TableRow key={`${order.orderId}-${order.orderNumber}`} hover>
              <TableCell sx={{ fontWeight: 900, color: '#172033' }}>
                {order.orderNumber || order.externalRef || 'N/A'}
                {order.mintsoftOrderNumber && order.mintsoftOrderNumber !== order.orderNumber && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    Mintsoft {order.mintsoftOrderNumber}
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                  {order.clientName || 'N/A'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {order.category || 'No category'}
                </Typography>
              </TableCell>
              <TableCell>{formatDate(order.dispatchedDate)}</TableCell>
              <TableCell>{order.shippingCarrier || 'N/A'}</TableCell>
              <TableCell>
                {order.tracking ? (
                  <Typography variant="body2" sx={{ fontWeight: 800, overflowWrap: 'anywhere' }}>
                    {order.tracking}
                  </Typography>
                ) : (
                  <Chip label="No Linx tracking" size="small" color="warning" variant="outlined" />
                )}
              </TableCell>
              <TableCell align="right">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<LocalShipping />}
                  onClick={() => onTrack(order)}
                  sx={{
                    minHeight: 34,
                    px: 1.35,
                    borderRadius: 1,
                    fontWeight: 900,
                    whiteSpace: 'nowrap',
                  }}
                >
                  Track
                </Button>
              </TableCell>
            </TableRow>
          ))}
          {!loading && orders.length === 0 && (
            <TableRow>
              <TableCell colSpan={6}>
                <Box sx={{ py: 5, textAlign: 'center' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#334155' }}>
                    No dispatched orders found
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Adjust the date range or client filter.
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  </Paper>
);

const ClaimDialog = ({ open, onClose, result }) => {
  const { showSnackbar } = useSnackbar();
  const defaults = result?.actions?.claimDefaults || EMPTY_OBJECT;
  const mintsoftItems = result?.systems?.mintsoft?.order?.orderItems || EMPTY_ARRAY;
  const buildClaimState = (overrides = EMPTY_OBJECT) => {
    const nextClaim = { claimType: 'missing', ...defaults, ...overrides };
    return {
      ...nextClaim,
      claimValue: normalizeGbpClaimValue(nextClaim.claimValue),
    };
  };
  const [claim, setClaim] = useState(() => buildClaimState());
  const [selectedItemKeys, setSelectedItemKeys] = useState([]);
  const [evidenceFiles, setEvidenceFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [emailPreview, setEmailPreview] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  React.useEffect(() => {
    if (open) {
      const defaultSelectedKeys = mintsoftItems.map(getItemKey);
      setSelectedItemKeys(defaultSelectedKeys);
      setClaim(buildClaimState({
        goodsDescription: buildGoodsDescription(mintsoftItems, defaultSelectedKeys) || defaults.goodsDescription || '',
      }));
      setEvidenceFiles([]);
      setEmailPreview(null);
      setConfirmOpen(false);
    }
  }, [open, defaults, mintsoftItems]);

  const updateClaim = (field, value) => {
    setClaim((prev) => ({ ...prev, [field]: value }));
  };

  const updateClaimValue = (value) => {
    updateClaim('claimValue', normalizeGbpClaimValue(value));
  };

  const updateSelectedItems = (nextKeys) => {
    setSelectedItemKeys(nextKeys);
    setClaim((prev) => ({
      ...prev,
      goodsDescription: buildGoodsDescription(mintsoftItems, nextKeys) || prev.goodsDescription || '',
    }));
  };

  const handlePreview = async () => {
    setBusy(true);
    try {
      const blob = await previewApcClaim(claim);
      downloadBlob(blob, getClaimFilename(claim));
      showSnackbar('APC claim preview downloaded.', 'success');
    } catch (error) {
      showSnackbar(error.response?.data?.message || error.message || 'Failed to generate claim preview.', 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleEvidenceSelect = (files) => {
    setEvidenceFiles(files);
    if (files.length) {
      showSnackbar(`${files.length} evidence file${files.length > 1 ? 's' : ''} ready to attach.`, 'success');
    }
  };

  const removeEvidenceFile = (fileToRemove) => {
    setEvidenceFiles((prev) => prev.filter((file) => file !== fileToRemove));
  };

  const handleSend = async () => {
    setBusy(true);
    try {
      const preview = await previewApcClaimEmail(claim);
      setEmailPreview(preview);
      setConfirmOpen(true);
    } catch (error) {
      showSnackbar(error.response?.data?.message || error.message || 'Failed to prepare claim email preview.', 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleConfirmSend = async () => {
    setBusy(true);
    try {
      await sendApcClaim(claim, evidenceFiles);
      showSnackbar('APC claim email sent.', 'success');
      setConfirmOpen(false);
      onClose();
    } catch (error) {
      showSnackbar(error.response?.data?.message || error.message || 'Failed to send claim email.', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={busy ? undefined : onClose}
        maxWidth={false}
        PaperProps={{
          sx: {
            borderRadius: 2,
            width: 'min(1120px, calc(100vw - 64px))',
            maxWidth: 'none',
          },
        }}
      >
        <DialogTitle sx={{ px: 3, py: 2.25 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 950, color: '#0f172a' }}>
                APC Claim
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Review claim details, attach evidence, then preview the email before sending.
              </Typography>
            </Box>
            <Chip color="primary" label={claim.claimType === 'damage' ? 'Damage claim' : 'Missing item claim'} sx={{ alignSelf: { xs: 'flex-start', sm: 'center' }, fontWeight: 900 }} />
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: '#f8fafc', p: 0 }}>
          <Stack
            spacing={2}
            sx={{
              width: '100%',
              p: 2.25,
              boxSizing: 'border-box',
              alignItems: 'stretch',
              '& .claim-dialog-section': {
                width: '100%',
                maxWidth: 'none',
                boxSizing: 'border-box',
                alignSelf: 'stretch',
              },
            }}
          >
            <Box sx={{ width: '100%', maxWidth: 'none', minWidth: 0 }}>
              <Stack spacing={2} sx={{ width: '100%', maxWidth: 'none', alignItems: 'stretch' }}>
                <Paper className="claim-dialog-section" variant="outlined" sx={{ p: 1.5, borderRadius: 1.5, bgcolor: '#fff' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 950, color: '#172033', mb: 1 }}>
                    Claim Type
                  </Typography>
                  <FormControl fullWidth size="small">
                    <InputLabel id="claim-type-label">Claim Type</InputLabel>
                    <Select
                      labelId="claim-type-label"
                      label="Claim Type"
                      value={claim.claimType}
                      onChange={(event) => updateClaim('claimType', event.target.value)}
                    >
                      <MenuItem value="missing">Missing item</MenuItem>
                      <MenuItem value="damage">Damage</MenuItem>
                    </Select>
                  </FormControl>
                </Paper>

                {mintsoftItems.length > 0 && (
                  <Paper className="claim-dialog-section" variant="outlined" sx={{ p: 1.5, borderRadius: 1.5, bgcolor: '#fff' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 950, color: '#172033', mb: 0.5 }}>
                      Claim Items
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
                      Selected products are used to build the description of goods with quantity and product text only.
                    </Typography>
                    <OrderItemCards
                      items={mintsoftItems}
                      selectedKeys={selectedItemKeys}
                      onSelectionChange={updateSelectedItems}
                      selectable
                      source="Mintsoft"
                    />
                  </Paper>
                )}

                <Alert className="claim-dialog-section" severity="info" sx={{ borderRadius: 1.5 }}>
                  <Stack component="ul" sx={{ m: 0, pl: 2 }}>
                    {CLAIM_RULES.slice(0, 3).map((rule) => (
                      <Typography component="li" variant="body2" key={rule}>{rule}</Typography>
                    ))}
                  </Stack>
                </Alert>
              </Stack>
            </Box>

            <Box sx={{ width: '100%', maxWidth: 'none', minWidth: 0 }}>
              <Stack spacing={2} sx={{ width: '100%', maxWidth: 'none', alignItems: 'stretch' }}>
                <Paper className="claim-dialog-section" variant="outlined" sx={{ p: 2, borderRadius: 1.5, bgcolor: '#fff' }}>
                  <Box sx={{ mb: 1.75 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 950, color: '#172033' }}>
                      Claim Form Details
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                      Confirm the courier claim fields before generating the document.
                    </Typography>
                  </Box>

                  <Stack spacing={2}>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(3, minmax(0, 1fr))' },
                        gap: 1.5,
                        width: '100%',
                      }}
                    >
                      {[
                        ['customerName', 'Customer Name'],
                        ['contactName', 'Contact Name'],
                        ['despatchDate', 'Despatch Date'],
                        ['consignmentNumber', 'Consignment Number'],
                        ['legalOwner', 'Legal Owner'],
                      ].map(([field, label]) => (
                        <TextField
                          key={field}
                          fullWidth
                          size="small"
                          label={label}
                          value={claim[field] || ''}
                          onChange={(event) => updateClaim(field, event.target.value)}
                        />
                      ))}
                      <TextField
                        fullWidth
                        size="small"
                        label="Full Claim Value"
                        value={getClaimValueInput(claim.claimValue)}
                        onChange={(event) => updateClaimValue(event.target.value)}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">{GBP_SYMBOL}</InputAdornment>,
                        }}
                        inputProps={{ inputMode: 'decimal' }}
                      />
                    </Box>

                    <TextField
                      fullWidth
                      size="small"
                      label="Description of Goods"
                      value={claim.goodsDescription || ''}
                      onChange={(event) => updateClaim('goodsDescription', event.target.value)}
                      multiline
                      minRows={7}
                      helperText="Auto-filled from selected Mintsoft products. Edit if the claim needs clearer product wording."
                    />

                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' },
                        gap: 1.5,
                        width: '100%',
                      }}
                    >
                      <TextField
                        fullWidth
                        size="small"
                        label="Current Location of Goods"
                        value={claim.goodsLocation || ''}
                        onChange={(event) => updateClaim('goodsLocation', event.target.value)}
                      />
                      {[
                        ['salvageValue', 'Salvage Value'],
                        ['damageItemPictures', 'Item Damage Photos Supplied'],
                        ['damagePackagingPictures', 'Packaging Damage Photos Supplied'],
                      ].map(([field, label]) => (
                        <FormControl fullWidth size="small" key={field}>
                          <InputLabel id={`${field}-label`}>{label}</InputLabel>
                          <Select
                            labelId={`${field}-label`}
                            label={label}
                            value={claim[field] || ''}
                            onChange={(event) => updateClaim(field, event.target.value)}
                          >
                            <MenuItem value="Yes">Yes</MenuItem>
                            <MenuItem value="No">No</MenuItem>
                          </Select>
                        </FormControl>
                      ))}
                    </Box>

                    <TextField
                      fullWidth
                      size="small"
                      label="Email Notes"
                      value={claim.notes || ''}
                      onChange={(event) => updateClaim('notes', event.target.value)}
                      multiline
                      minRows={4}
                    />
                  </Stack>
                </Paper>

                <Paper className="claim-dialog-section" variant="outlined" sx={{ p: 1.75, borderRadius: 1.5, bgcolor: '#fff' }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.25}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 950, color: '#172033' }}>
                        Evidence Attachments
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Files are attached to the courier email when you confirm send.
                      </Typography>
                    </Box>
                    <Button component="label" variant="outlined" startIcon={<AttachFile />} sx={{ alignSelf: { xs: 'flex-start', sm: 'center' }, borderRadius: 1, fontWeight: 900 }}>
                      Add Files
                      <input
                        hidden
                        type="file"
                        multiple
                        onChange={(event) => handleEvidenceSelect(Array.from(event.target.files || []))}
                      />
                    </Button>
                  </Stack>

                  <Divider sx={{ my: 1.5 }} />

                  {evidenceFiles.length > 0 ? (
                    <Stack spacing={1}>
                      {evidenceFiles.map((file) => (
                        <Stack
                          key={`${file.name}-${file.size}-${file.lastModified}`}
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          spacing={1}
                          sx={{ p: 1, border: '1px solid #e2e8f0', borderRadius: 1, bgcolor: '#f8fafc' }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 800, overflowWrap: 'anywhere' }}>
                              {file.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatFileSize(file.size)}
                            </Typography>
                          </Box>
                          <Button size="small" color="error" onClick={() => removeEvidenceFile(file)}>
                            Remove
                          </Button>
                        </Stack>
                      ))}
                    </Stack>
                  ) : (
                    <Alert severity="warning" sx={{ borderRadius: 1 }}>
                      No evidence files selected yet.
                    </Alert>
                  )}
                </Paper>
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5, bgcolor: '#fff' }}>
          <Button onClick={onClose} disabled={busy} sx={{ borderRadius: 1 }}>Cancel</Button>
          <Button onClick={handlePreview} disabled={busy} startIcon={<Download />} variant="outlined" sx={{ borderRadius: 1 }}>
            Preview DOCX
          </Button>
          <Button onClick={handleSend} disabled={busy} startIcon={busy ? <CircularProgress size={16} /> : <VisibilityOutlined />} variant="contained" sx={{ borderRadius: 1 }}>
            Preview Email
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmOpen} onClose={busy ? undefined : () => setConfirmOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 950 }}>Confirm APC Email</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <Alert severity="info" sx={{ borderRadius: 1 }}>
              Review the courier email below. It will include the claim form and {evidenceFiles.length} evidence file{evidenceFiles.length === 1 ? '' : 's'}.
            </Alert>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={6}>
                <FactCell label="To" value={emailPreview?.to} strong />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FactCell label="From" value={emailPreview?.from} />
              </Grid>
              <Grid item xs={12}>
                <FactCell label="Subject" value={emailPreview?.subject} strong />
              </Grid>
            </Grid>
            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1, bgcolor: '#fff' }}>
              <Box dangerouslySetInnerHTML={{ __html: emailPreview?.html || '' }} />
            </Paper>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 950, mb: 0.75 }}>
                Attachments
              </Typography>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                {(emailPreview?.attachments || []).map((attachment) => (
                  <Chip key={attachment.filename} label={attachment.filename} size="small" />
                ))}
                {evidenceFiles.map((file) => (
                  <Chip key={`${file.name}-${file.size}`} label={file.name} size="small" variant="outlined" />
                ))}
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={() => setConfirmOpen(false)} disabled={busy} sx={{ borderRadius: 1 }}>Back to Edit</Button>
          <Button onClick={handleConfirmSend} disabled={busy} startIcon={busy ? <CircularProgress size={16} /> : <AlternateEmailOutlined />} variant="contained" sx={{ borderRadius: 1 }}>
            Confirm Send to APC
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const OrderTracker = () => {
  const { showSnackbar } = useSnackbar();
  const [mode, setMode] = useState('orderNumber');
  const [orderNumber, setOrderNumber] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [courier, setCourier] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [claimOpen, setClaimOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('courier');
  const [queueFilters, setQueueFilters] = useState(getDefaultQueueFilters);
  const [dispatchedOrders, setDispatchedOrders] = useState([]);
  const [clientOptions, setClientOptions] = useState([]);
  const [queueLoading, setQueueLoading] = useState(false);
  const [queueError, setQueueError] = useState('');

  const localOrder = result?.localOrder;
  const tracking = result?.tracking;
  const latestEvent = getCurrentTrackingEvent(tracking);
  const canGenerateApcClaim = Boolean(result?.actions?.canGenerateApcClaim);

  const loadDispatchedOrders = async (nextFilters = queueFilters) => {
    setQueueLoading(true);
    setQueueError('');
    try {
      const data = await getOrderTrackerDispatchedOrders({
        ...nextFilters,
        limit: 200,
      });
      const nextOrders = data.orders || [];
      const responseClientOptions = Array.isArray(data.clientOptions) ? data.clientOptions : [];
      const fallbackClientOptions = nextFilters.clientName
        ? clientOptions
        : nextOrders.map((order) => order.clientName).filter(Boolean);
      const nextClientOptions = [
        ...new Set([
          ...responseClientOptions,
          ...fallbackClientOptions,
          nextFilters.clientName,
        ].filter(Boolean)),
      ].sort();
      setDispatchedOrders(nextOrders);
      setClientOptions(nextClientOptions);
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Failed to load dispatched orders.';
      setQueueError(message);
      showSnackbar(message, 'error');
    } finally {
      setQueueLoading(false);
    }
  };

  useEffect(() => {
    loadDispatchedOrders(getDefaultQueueFilters());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateQueueFilter = (field, value) => {
    setQueueFilters((prev) => ({ ...prev, [field]: value }));
  };

  const applyQueueRangePreset = (rangePreset) => {
    const nextFilters = {
      ...queueFilters,
      ...getQueueDateRangePreset(rangePreset),
      rangePreset,
    };
    setQueueFilters((prev) => ({
      ...prev,
      ...nextFilters,
    }));
    loadDispatchedOrders(nextFilters);
  };

  const handleSearch = async (override = {}) => {
    const nextMode = override.mode || mode;
    const nextOrderNumber = override.orderNumber ?? orderNumber;
    const nextTrackingNumber = override.trackingNumber ?? trackingNumber;
    const nextCourier = override.courier ?? courier;
    const nextHasSearchValue = nextMode === 'orderNumber'
      ? nextOrderNumber.trim()
      : nextTrackingNumber.trim();

    if (!nextHasSearchValue) {
      showSnackbar('Enter an order number or tracking number.', 'warning');
      return;
    }
    setLoading(true);
    try {
      const data = await searchOrderTracker({
        orderNumber: nextMode === 'orderNumber' ? nextOrderNumber.trim() : '',
        trackingNumber: nextMode === 'trackingNumber' ? nextTrackingNumber.trim() : '',
        courier: nextCourier || undefined,
      });
      setResult(data);
      setActiveTab('courier');
    } catch (error) {
      showSnackbar(error.response?.data?.message || error.message || 'Order tracker search failed.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleTrackDispatchedOrder = async (order) => {
    const nextOrderNumber = order.orderNumber || order.externalRef || order.mintsoftOrderNumber || '';
    const nextTrackingNumber = order.tracking || '';
    if (!nextOrderNumber && !nextTrackingNumber) {
      showSnackbar('This dispatched order has no searchable order number or tracking number.', 'warning');
      return;
    }

    const nextMode = nextOrderNumber ? 'orderNumber' : 'trackingNumber';
    setMode(nextMode);
    setOrderNumber(nextOrderNumber);
    setTrackingNumber(nextTrackingNumber);
    await handleSearch({
      mode: nextMode,
      orderNumber: nextOrderNumber,
      trackingNumber: nextTrackingNumber,
    });
  };

  const backToQueue = () => {
    setResult(null);
    setClaimOpen(false);
    setActiveTab('courier');
  };

  const searchMissingTracking = async () => {
    if (!trackingNumber.trim()) {
      showSnackbar('Enter a tracking number.', 'warning');
      return;
    }
    setMode('trackingNumber');
    await handleSearch({ mode: 'trackingNumber', trackingNumber });
  };

  return (
    <Layout>
      <Box sx={{ width: '100%', maxWidth: 'none', alignSelf: 'stretch', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            borderRadius: 1,
            bgcolor: '#fff',
            borderColor: '#d7e0ea',
            boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
          }}
        >
          <Stack spacing={1.5}>
            <Stack direction={{ xs: 'column', lg: 'row' }} justifyContent="space-between" alignItems={{ lg: 'flex-start' }} spacing={1.5}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 950, color: '#172033', lineHeight: 1.15 }}>
                  Order Tracker
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                  Search once, review every system, then decide the next CS action.
                </Typography>
              </Box>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ minWidth: { lg: 420 } }}>
                <ToggleButtonGroup
                  value={mode}
                  exclusive
                  onChange={(_, value) => value && setMode(value)}
                  size="small"
                  aria-label="Search mode"
                  sx={{
                    '& .MuiToggleButton-root': {
                      px: 1.6,
                      fontWeight: 900,
                      borderRadius: '4px !important',
                    },
                  }}
                >
                  <ToggleButton value="orderNumber">Order</ToggleButton>
                  <ToggleButton value="trackingNumber">Tracking</ToggleButton>
                </ToggleButtonGroup>
                <FormControl size="small" sx={{ minWidth: 170 }}>
                  <InputLabel id="courier-label" shrink>Courier</InputLabel>
                  <Select
                    labelId="courier-label"
                    label="Courier"
                    value={courier}
                    displayEmpty
                    renderValue={(selected) => {
                      if (!selected) return 'Auto';
                      if (selected === 'apc') return 'APC';
                      if (selected === 'royal_mail') return 'Royal Mail';
                      return selected;
                    }}
                    onChange={(event) => setCourier(event.target.value)}
                  >
                    <MenuItem value="">Auto</MenuItem>
                    <MenuItem value="apc">APC</MenuItem>
                    <MenuItem value="royal_mail">Royal Mail</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            </Stack>

            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1}
              component="form"
              onSubmit={(event) => {
                event.preventDefault();
                handleSearch();
              }}
            >
              {mode === 'orderNumber' ? (
                <TextField
                  label="Order Number"
                  value={orderNumber}
                  onChange={(event) => setOrderNumber(event.target.value)}
                  size="small"
                  fullWidth
                />
              ) : (
                <TextField
                  label="Tracking Number"
                  value={trackingNumber}
                  onChange={(event) => setTrackingNumber(event.target.value)}
                  size="small"
                  fullWidth
                />
              )}
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={16} /> : <SearchRounded />}
                sx={{
                  height: 40,
                  minWidth: 136,
                  px: 2.25,
                  borderRadius: 1,
                  fontWeight: 900,
                  alignSelf: { xs: 'stretch', md: 'center' },
                  whiteSpace: 'nowrap',
                }}
              >
                Search
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {result && (
          <Button
            variant="text"
            startIcon={<ArrowBack />}
            onClick={backToQueue}
            sx={{
              alignSelf: 'flex-start',
              borderRadius: 1,
              fontWeight: 900,
              color: 'secondary.main',
              px: 0.75,
            }}
          >
            Back to dispatched orders
          </Button>
        )}

        {!result && (
          <>
            {queueError && (
              <Alert severity="error" sx={{ borderRadius: 1 }}>
                {queueError}
              </Alert>
            )}
            <DispatchedOrdersQueue
              filters={queueFilters}
              onFilterChange={updateQueueFilter}
              onRangePresetChange={applyQueueRangePreset}
              onApplyFilters={() => loadDispatchedOrders()}
              loading={queueLoading}
              orders={dispatchedOrders}
              clientOptions={clientOptions}
              onTrack={handleTrackDispatchedOrder}
            />
          </>
        )}

        {result && (
          <>
            <StatusOverview
              localOrder={localOrder}
              tracking={tracking}
              latestEvent={latestEvent}
              result={result}
              canGenerateApcClaim={canGenerateApcClaim}
              onClaimOpen={() => setClaimOpen(true)}
            />

            {tracking?.requiresManualTracking && (
              <Alert severity="warning" sx={{ borderRadius: 1 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    This order has no tracking number in Linx.
                  </Typography>
                  <TextField
                    label="Tracking Number"
                    value={trackingNumber}
                    onChange={(event) => setTrackingNumber(event.target.value)}
                    size="small"
                    sx={{ minWidth: 240 }}
                  />
                  <Button variant="outlined" onClick={searchMissingTracking} sx={{ borderRadius: 1 }}>
                    Track
                  </Button>
                </Stack>
              </Alert>
            )}

            <OrderDetailsTabs
              activeTab={activeTab}
              onTabChange={setActiveTab}
              localOrder={localOrder}
              result={result}
              tracking={tracking}
            />
          </>
        )}
      </Box>

      <ClaimDialog open={claimOpen} onClose={() => setClaimOpen(false)} result={result} />
    </Layout>
  );
};

export default OrderTracker;
